import L from 'leaflet';
import { plantillaDeMosaicos, type Vuelo } from '../services/flights';
import { agregarSuperposicion } from './controlDeCapas';

/**
 * Preferencia de fondo: imagen cargada o satelital.
 *
 * Va en localStorage y es de los pocos casos donde corresponde: es una
 * preferencia de VISTA de esta persona en este navegador, no un dato del
 * cliente. Nadie más necesita saber qué fondo prefiere, y perderla al
 * limpiar el navegador no cuesta nada.
 */
const PREF_FONDO = 'mapa_fondo_ortofoto';

/**
 * Pone la imagen georreferenciada del cliente como fondo del mapa.
 *
 * Se comparte entre todos los mapas de la plataforma —dashboard, suelo,
 * clima y ubicaciones— para que el fondo se vea y se comporte igual en
 * todos. Duplicar esto por pantalla sería garantizar que se separen.
 *
 * @returns función de limpieza, para llamar al desmontar el mapa.
 */
export function agregarCapaOrtofoto(map: L.Map, vuelo: Vuelo, etiqueta: string): () => void {
  const { minLat, minLng, maxLat, maxLng } = vuelo.limites;

  const capa = L.tileLayer(plantillaDeMosaicos(vuelo.id), {
    minZoom: vuelo.minZoom,
    maxZoom: 21,
    // Arriba del nivel que se cortó, Leaflet estira el último en vez de
    // pedir mosaicos que no existen: acercarse de más no deja la pantalla
    // en blanco, sólo más borrosa.
    maxNativeZoom: vuelo.maxZoom,
    bounds: L.latLngBounds([minLat, minLng], [maxLat, maxLng]),
    // Los bordes de la zona cubierta tienen huecos por diseño y el servidor
    // contesta 204; sin esto Leaflet pinta el ícono de imagen rota.
    errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
  });

  if (localStorage.getItem(PREF_FONDO) !== 'no') capa.addTo(map);

  // El control de la esquina es uno solo y lo comparte con el radar; antes
  // esta función creaba el suyo propio y salían dos cajitas apiladas.
  const quitar = agregarSuperposicion(map, capa, etiqueta);

  const recordar = (e: L.LayersControlEvent) => {
    if (e.layer === capa) localStorage.setItem(PREF_FONDO, e.type === 'overlayadd' ? 'si' : 'no');
  };
  map.on('overlayadd', recordar);
  map.on('overlayremove', recordar);

  return () => {
    map.off('overlayadd', recordar);
    map.off('overlayremove', recordar);
    quitar();
  };
}
